/**
 * PieChart - Pie/Doughnut chart wrapper using react-chartjs-2 with EduQ brand colors
 * 
 * Features:
 * - EduQ brand color scheme
 * - Pie or Doughnut style
 * - Responsive design
 * - Customizable options
 * - Loading state
 * - Empty state
 */

import React from 'react';
import { Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// EduQ brand colors
const BRAND_COLORS = {
  blue: '#0066FF',
  cyan: '#00D9B5',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',
  secondary: '#6B7280',
  purple: '#8B5CF6',
  pink: '#EC4899',
  indigo: '#6366F1'
};

const DEFAULT_COLORS = [
  BRAND_COLORS.blue,
  BRAND_COLORS.cyan,
  BRAND_COLORS.success,
  BRAND_COLORS.warning,
  BRAND_COLORS.info,
  BRAND_COLORS.error,
  BRAND_COLORS.purple,
  BRAND_COLORS.pink,
  BRAND_COLORS.indigo,
  BRAND_COLORS.secondary
];

const PieChart = ({
  data,
  title,
  height = 300,
  loading = false,
  emptyMessage = 'No data available',
  options = {},
  colors = DEFAULT_COLORS,
  variant = 'pie', // 'pie' or 'doughnut'
  showLegend = true,
  showPercentage = true,
  cutout = '50%', // Only for doughnut
  className = ''
}) => {
  if (loading) {
    return (
      <div className={`admin-chart admin-chart--loading ${className}`} style={{ height }}>
        <div className="admin-chart__loading">
          <div className="admin-spinner"></div>
          <p>Loading chart...</p>
        </div>
      </div>
    );
  }

  if (!data || !data.labels || data.labels.length === 0) {
    return (
      <div className={`admin-chart admin-chart--empty ${className}`} style={{ height }}>
        <div className="admin-chart__empty">
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Calculate total for percentages
  const total = data.datasets?.[0]?.data?.reduce((sum, val) => sum + val, 0) || 0;

  // Apply colors to dataset
  const chartData = {
    ...data,
    datasets: data.datasets.map((dataset) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || colors.slice(0, data.labels.length),
      borderColor: dataset.borderColor || '#FFFFFF',
      borderWidth: dataset.borderWidth || 2,
      hoverOffset: dataset.hoverOffset || 4
    }))
  };

  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: showLegend,
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          },
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            if (!datasets.length) return [];

            return chart.data.labels.map((label, i) => {
              const value = datasets[0].data[i];
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              
              return {
                text: showPercentage ? `${label} (${percentage}%)` : label,
                fillStyle: datasets[0].backgroundColor[i],
                hidden: false,
                index: i
              };
            });
          }
        }
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 16,
          weight: '600',
          family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        },
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: '600'
        },
        bodyFont: {
          size: 12
        },
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        displayColors: true,
        usePointStyle: true,
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  // Add cutout for doughnut
  if (variant === 'doughnut') {
    defaultOptions.cutout = cutout;
  }

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins
    }
  };

  const ChartComponent = variant === 'doughnut' ? Doughnut : Pie;

  // Generate accessible description for screen readers
  const generateAriaLabel = () => {
    if (!data || !data.labels || data.labels.length === 0) {
      return 'Empty chart';
    }
    
    const descriptions = data.labels.map((label, i) => {
      const value = data.datasets[0].data[i];
      const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
      return `${label}: ${value} (${percentage}%)`;
    });
    
    return `${title || 'Chart'}. ${descriptions.join(', ')}`;
  };

  return (
    <div 
      className={`admin-chart ${className}`} 
      style={{ height }}
      role="img"
      aria-label={generateAriaLabel()}
      tabIndex={0}
    >
      <ChartComponent data={chartData} options={mergedOptions} />
      
      {/* Text alternative for accessibility */}
      <div className="admin-sr-only">
        <p>{title || 'Chart data'}</p>
        <ul>
          {data.labels && data.labels.map((label, i) => {
            const value = data.datasets[0].data[i];
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return (
              <li key={i}>
                {label}: {value} ({percentage}%)
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default PieChart;
